/**
 * use-preview-schedule
 *
 * Generates the preview schedule data (phases + tasks) from current form state.
 * Also computes the timeline scroll config for auto-scrolling to first phase/task.
 */

import { useMemo, useCallback } from 'react'
import { useCropModalContext } from '../context/crop-modal-context'
import { generatePreviewScheduleData } from '../generators/generate-preview-schedule-data'
import { generateTasksFromSchedule } from '../generators/generate-tasks-from-schedule'
import { CROP_TASK_TYPES } from '../constants/crop-modal-constants'

export const usePreviewSchedule = ({
  cropPhaseTypes,
  effectivePhaseCodes,
  effectiveTaskCodes,
  effectivePropagationCodes,
  activePropagationCode,
  frostDates,
  effectiveTemplate,
  collectionTypes,
  farmClimate,
}) => {
  const {
    archetype,
    species,
    indoorOffset,
    outdoorOffset,
    dtm,
    growthType,
    lifecycleType,
    adjustedPhases,
    adjustedTasks,
    cropCreatedAt,
    successions,
    isCustomCrop,
  } = useCropModalContext()

  // Custom crop: sequential stub phases from today, 14-day default each
  const customStubPhases = useMemo(() => {
    if (!isCustomCrop) return []
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    let cursor = new Date(today)
    return (effectivePhaseCodes ?? []).map((code, i) => {
      const start = cursor.toISOString()
      const next = new Date(cursor)
      next.setDate(next.getDate() + 14)
      cursor = next
      const phaseType = cropPhaseTypes?.find(pt => pt.code === code)
      return {
        id: `preview-phase-${i + 1}`,
        phase_type_code: code,
        phase_type: phaseType ?? { code, name: code },
        est_started_at: start,
        est_duration: '14 days',
        started_at: null,
        elapsed_time: null,
        step: i + 1,
        state: 'scheduled',
      }
    })
  }, [isCustomCrop, effectivePhaseCodes, cropPhaseTypes])

  // Custom crop: default tasks generated from stub phases + selected task codes
  const customDefaultTasks = useMemo(() => {
    if (!isCustomCrop || !customStubPhases.length) return []
    const allTaskCodes = CROP_TASK_TYPES.map(t => t.code)
    const skipTaskTypes = allTaskCodes.filter(code => !effectiveTaskCodes?.includes(code))
    const stubCrop = {
      id: 'preview',
      organization_id: null,
      template_crop_species: {
        name: '',
        propagation_codes: activePropagationCode ? [activePropagationCode] : [],
      },
    }
    const fallbackDate = customStubPhases[0]?.est_started_at
      ? new Date(customStubPhases[0].est_started_at)
      : new Date()
    return generateTasksFromSchedule(
      { crop: stubCrop, schedule: { id: 'preview-sched' }, phases: customStubPhases },
      { userId: '00000000-0000-0000-0000-000000000000', skipTaskTypes, state: 'ready', fallbackDate }
    )
  }, [isCustomCrop, customStubPhases, effectiveTaskCodes, activePropagationCode])

  const customPreviewData = useMemo(() => {
    if (!isCustomCrop) return null
    const stubCrop = {
      id: 'preview',
      organization_id: null,
      template_crop_species: {
        name: '',
        propagation_codes: activePropagationCode ? [activePropagationCode] : [],
      },
    }
    return [{
      crop: stubCrop,
      schedule: { id: 'preview-sched', crop_id: 'preview', state: 'active' },
      phases: adjustedPhases ?? customStubPhases,
      tasks: adjustedTasks ?? customDefaultTasks,
    }]
  }, [isCustomCrop, customStubPhases, customDefaultTasks, adjustedPhases, adjustedTasks, activePropagationCode])

  // Base (archetype-generated) data — not affected by adjustedPhases/adjustedTasks
  const baseData = useMemo(() => {
    if (isCustomCrop) return null
    if (!archetype || !species) return null

    const templateForSchedule = effectiveTemplate ?? species
    const collection = collectionTypes?.find(c => c.code === templateForSchedule.collection_code) ?? null

    return generatePreviewScheduleData({
      archetype,
      species: templateForSchedule,
      frostDates,
      overrides: { indoorOffset, outdoorOffset, dtm },
      phaseTypes: cropPhaseTypes,
      selectedPhaseCodes: effectivePhaseCodes,
      growthType: growthType ?? templateForSchedule.growth_code,
      propagationCode: activePropagationCode,
      lifecycleType,
      cropCreatedAt,
      successions,
      collection,
      farmClimate,
    })
  }, [
    isCustomCrop, archetype, species, effectiveTemplate, frostDates,
    indoorOffset, outdoorOffset, dtm,
    cropPhaseTypes, effectivePhaseCodes,
    growthType, lifecycleType, cropCreatedAt,
    activePropagationCode, successions,
    collectionTypes, farmClimate,
  ])

  // Default (unadjusted) tasks from variety/species — variety if selected, species otherwise.
  // Uses adjustedPhases when set so tasks land at the user's current schedule position.
  const defaultTasks = useMemo(() => {
    if (isCustomCrop) return customDefaultTasks
    if (!baseData?.[0]) return []

    const templateForSchedule = effectiveTemplate ?? species
    const allTaskCodes = CROP_TASK_TYPES.map(t => t.code)
    const skipTaskTypes = allTaskCodes.filter(code => !effectiveTaskCodes?.includes(code))

    return generateTasksFromSchedule({
      crop: {
        ...baseData[0].crop,
        template_crop_species: {
          ...templateForSchedule,
          propagation_codes: activePropagationCode ? [activePropagationCode] : effectivePropagationCodes
        }
      },
      schedule: baseData[0].schedule,
      phases: adjustedPhases ?? baseData[0].phases,
    }, {
      userId: '00000000-0000-0000-0000-000000000000',
      skipTaskTypes,
      state: 'ready'
    })
  }, [isCustomCrop, customDefaultTasks, baseData, species, effectiveTemplate, effectiveTaskCodes, effectivePropagationCodes, activePropagationCode, adjustedPhases])

  // Generate a single task from current phase positions — used by handleTaskToggle for surgical add.
  const generateTaskFromCurrentPhases = useCallback((taskCode) => {
    if (isCustomCrop) {
      if (!customStubPhases.length) return null
      const stubCrop = {
        id: 'preview',
        organization_id: null,
        template_crop_species: {
          name: '',
          propagation_codes: activePropagationCode ? [activePropagationCode] : [],
        },
      }
      const allTaskCodes = CROP_TASK_TYPES.map(t => t.code)
      const skipTaskTypes = allTaskCodes.filter(c => c !== taskCode)
      const tasks = generateTasksFromSchedule(
        { crop: stubCrop, schedule: { id: 'preview-sched' }, phases: adjustedPhases ?? customStubPhases },
        { userId: '00000000-0000-0000-0000-000000000000', skipTaskTypes, state: 'ready' }
      )
      return tasks[0] ?? null
    }
    if (!baseData?.[0]) return null
    const templateForSchedule = effectiveTemplate ?? species
    const currentPhases = adjustedPhases ?? baseData[0].phases
    const allTaskCodes = CROP_TASK_TYPES.map(t => t.code)
    const skipTaskTypes = allTaskCodes.filter(c => c !== taskCode)
    const tasks = generateTasksFromSchedule({
      crop: {
        ...baseData[0].crop,
        template_crop_species: {
          ...templateForSchedule,
          propagation_codes: activePropagationCode ? [activePropagationCode] : effectivePropagationCodes,
        },
      },
      schedule: baseData[0].schedule,
      phases: currentPhases,
    }, { userId: '00000000-0000-0000-0000-000000000000', skipTaskTypes, state: 'ready' })
    return tasks[0] ?? null
  }, [isCustomCrop, customStubPhases, baseData, effectiveTemplate, species, adjustedPhases, activePropagationCode, effectivePropagationCodes])

  const previewScheduleData = useMemo(() => {
    if (isCustomCrop) return customPreviewData ?? []
    if (!baseData?.[0]) return []

    const templateForSchedule = effectiveTemplate ?? species
    const allTaskCodes = CROP_TASK_TYPES.map(t => t.code)
    const skipTaskTypes = allTaskCodes.filter(code => !effectiveTaskCodes?.includes(code))

    const phasesToUse = adjustedPhases || baseData[0].phases
    const tasksToUse = adjustedTasks || defaultTasks

    // Build all items: parent first, then succession items with auto-generated tasks
    return baseData.map((item, i) => {
      if (i === 0) {
        return { ...item, phases: phasesToUse, tasks: tasksToUse }
      }
      // Succession items: generate tasks from their offset phases
      const succTasks = generateTasksFromSchedule({
        crop: {
          ...item.crop,
          template_crop_species: {
            ...templateForSchedule,
            propagation_codes: activePropagationCode ? [activePropagationCode] : effectivePropagationCodes
          }
        },
        schedule: item.schedule,
        phases: item.phases,
      }, {
        userId: '00000000-0000-0000-0000-000000000000',
        skipTaskTypes,
        state: 'ready'
      })
      return { ...item, tasks: succTasks }
    })
  }, [isCustomCrop, customPreviewData, baseData, adjustedPhases, adjustedTasks, defaultTasks, species, effectiveTemplate, effectiveTaskCodes, effectivePropagationCodes, activePropagationCode])

  // Scroll config: anchored to the archetype planting date so phase/task toggles and
  // drag adjustments never trigger an unwanted viewport jump. Only re-fires when the
  // species (archetype) or farm frost dates actually change.
  const timelineScrollConfig = useMemo(() => {
    if (isCustomCrop) {
      // Custom crops start from today — scroll to 1 week before today
      const today = new Date()
      today.setHours(0, 0, 0, 0)
      today.setDate(today.getDate() - 7)
      return { scrollToDate: today, scrollToLeftOffset: 150 }
    }

    if (!frostDates?.lfd) return { scrollToDate: null, scrollToLeftOffset: undefined }

    // Use the archetype's default indoor offset (not the user override) so dragging
    // indoor-offset phases doesn't retrigger this scroll.
    const indoorOffsetWeeks = archetype?.indoor_sow_offset ?? 0
    const plantingMs = new Date(frostDates.lfd).getTime() + indoorOffsetWeeks * 7 * 86400000
    // Scroll to 1 week before planting to keep pre-phase tasks (sow_indoor etc.) visible
    return { scrollToDate: new Date(plantingMs - 7 * 86400000), scrollToLeftOffset: 150 }
  }, [isCustomCrop, frostDates, archetype])

  // Default (unadjusted) phases — for custom crops use stub phases (no adjustedPhases override)
  const defaultPhases = isCustomCrop
    ? customStubPhases
    : (baseData?.[0]?.phases ?? [])

  return { previewScheduleData, timelineScrollConfig, defaultPhases, defaultTasks, generateTaskFromCurrentPhases }
}
